import { Spinner } from "@ui/components/spinner";
import { cn } from "@ui/lib";
import { ChefHatIcon, ClockIcon, PlusIcon, UsersIcon } from "lucide-react";
import {
	AddAllButton,
	NoPhoto,
	QuantityStepper,
	type SetQuantity,
	usePending,
} from "./ProductCardView";
import { ShopperMarkdown } from "./ShopperMarkdown";
import {
	type BasketPick,
	formatMoney,
	type ProductCard,
	type RecipeInput,
	type RecipeOutput,
	recipeDuration,
	recipeLine,
} from "./types";

/**
 * What find_ingredients shows the shopper: the recipe the model wrote, filled
 * in as the arguments stream, each line with the product that answers it
 * once the tool has run. Pantry staples sit apart and stay out of "add all".
 */
export function RecipeCard({
	input,
	output,
	streaming,
	quantities,
	onQuantity,
	onAddAll,
	onOpen,
}: {
	input: RecipeInput | undefined;
	/** Undefined until the ingredients have been matched. */
	output: RecipeOutput | undefined;
	/** The model is still writing the recipe. */
	streaming: boolean;
	quantities: Map<string, number>;
	onQuantity: SetQuantity;
	onAddAll: (picks: BasketPick[]) => Promise<boolean>;
	onOpen: (product: ProductCard) => void;
}) {
	// It named what the store never sells: gone, and the assistant writes it again.
	if (output?.rejected) {
		return null;
	}
	const lines = recipeLines(input, output);
	const toBuy = lines.filter((line) => !line.pantry);
	const pantry = lines.filter((line) => line.pantry);
	const rowProps = {
		matched: Boolean(output),
		quantities,
		onQuantity,
		onOpen,
	};

	return (
		<section
			aria-label={input?.dish ? `Recipe: ${input.dish}` : "Recipe"}
			className="flex min-w-0 animate-shop-rise flex-col overflow-hidden rounded-2xl bg-(--shop-card) ring-1 ring-(--shop-brand-line)"
		>
			<RecipeHeader input={input} ingredients={lines.length} />

			{toBuy.length ? (
				<ul className="flex flex-col divide-y divide-(--shop-brand-line)/70 border-(--shop-brand-line)/70 border-t px-4">
					{toBuy.map((line) => (
						<IngredientRow
							key={line.position}
							line={line}
							{...rowProps}
						/>
					))}
				</ul>
			) : null}

			{pantry.length ? (
				<div className="border-(--shop-brand-line)/70 border-t bg-(--shop-surface) px-4 pt-2.5">
					<h4 className="font-semibold text-(--shop-muted) text-xs">
						You may already have
					</h4>
					<ul className="flex flex-col divide-y divide-(--shop-brand-line)/70">
						{pantry.map((line) => (
							<IngredientRow
								key={line.position}
								line={line}
								{...rowProps}
							/>
						))}
					</ul>
				</div>
			) : null}

			{output ? (
				<RecipeActions
					toBuy={toBuy}
					quantities={quantities}
					onAddAll={onAddAll}
				/>
			) : null}

			<RecipeSteps steps={input?.steps} streaming={streaming} />
		</section>
	);
}

/** A recipe line as the card shows it, with the product the tool matched to it. */
interface IngredientLine {
	/**
	 * The line's place in the model's `ingredients`: its identity. The tool's matches are
	 * keyed on it, and it holds while the line's words are still streaming in.
	 */
	position: number;
	text: string;
	pantry: boolean;
	/** Null until matched, and when the store does not sell it. */
	product: ProductCard | null;
	/** The packs of `product` the line needs. */
	quantity: number;
}

/** The lines written so far, each with its match once the tool has run. */
function recipeLines(
	input: RecipeInput | undefined,
	output: RecipeOutput | undefined,
): IngredientLine[] {
	return (input?.ingredients ?? []).flatMap((raw, position) => {
		const line = recipeLine(raw);
		if (!line) {
			return [];
		}
		const match = output?.matches[position];
		return [
			{
				...line,
				position,
				product: match?.best ?? null,
				quantity: match?.quantity ?? 1,
			},
		];
	});
}

/** The dish (a placeholder until it is written), servings, time and how many lines. */
function RecipeHeader({
	input,
	ingredients,
}: {
	input: RecipeInput | undefined;
	ingredients: number;
}) {
	return (
		<header className="flex flex-col gap-1.5 px-4 pt-3.5 pb-3">
			<p className="flex items-center gap-1.5 font-semibold text-(--shop-brand-text) text-[11px] uppercase tracking-wide">
				<ChefHatIcon className="size-3.5" aria-hidden />
				Recipe
			</p>
			<h3
				dir="auto"
				className="font-bold text-(--shop-ink) text-lg leading-tight"
			>
				{input?.dish || (
					<span className="block h-5 w-40 animate-pulse rounded-md bg-(--shop-brand-soft)" />
				)}
			</h3>
			<ul className="flex flex-wrap gap-x-3 gap-y-1 text-(--shop-muted) text-xs">
				{input?.servings ? (
					<Meta icon={UsersIcon}>Serves {input.servings}</Meta>
				) : null}
				{input?.minutes ? (
					<Meta icon={ClockIcon}>
						{recipeDuration(input.minutes)}
					</Meta>
				) : null}
				{ingredients ? (
					<li className="tabular">
						{ingredients} ingredient
						{ingredients === 1 ? "" : "s"}
					</li>
				) : null}
			</ul>
		</header>
	);
}

/** Once matched: what the store does not sell, and "add all" for what it does. */
function RecipeActions({
	toBuy,
	quantities,
	onAddAll,
}: {
	toBuy: IngredientLine[];
	quantities: Map<string, number>;
	onAddAll: (picks: BasketPick[]) => Promise<boolean>;
}) {
	const missing = toBuy
		.filter((line) => !line.product)
		.map((line) => line.text);
	return (
		<div className="flex flex-col gap-2 border-(--shop-brand-line)/70 border-t px-4 py-3">
			{missing.length ? (
				<p dir="auto" className="text-(--shop-muted) text-xs">
					Not in the store: {missing.join(", ")}
				</p>
			) : null}
			{toBuy.some((line) => line.product?.inStock) ? (
				<AddAllButton
					picks={toBuy.flatMap(({ product, quantity }) =>
						product ? [{ product, quantity }] : [],
					)}
					quantities={quantities}
					onAddAll={onAddAll}
				/>
			) : null}
		</div>
	);
}

/** The method, numbered, rendered as it streams. */
function RecipeSteps({
	steps: written,
	streaming,
}: {
	steps: RecipeInput["steps"];
	streaming: boolean;
}) {
	const steps = (written ?? []).filter((step): step is string =>
		Boolean(step?.trim()),
	);
	if (!steps.length) {
		return null;
	}
	return (
		<div className="flex flex-col gap-2 border-(--shop-brand-line)/70 border-t px-4 pt-3 pb-4">
			<h4 className="font-semibold text-(--shop-ink) text-sm">
				How to make it
			</h4>
			<ShopperMarkdown
				streaming={streaming}
				steps
				className="text-(--shop-ink) text-[14px] leading-snug"
			>
				{steps.map((step, i) => `${i + 1}. ${step}`).join("\n")}
			</ShopperMarkdown>
		</div>
	);
}

function IngredientRow({
	line,
	matched,
	quantities,
	onQuantity,
	onOpen,
}: {
	line: IngredientLine;
	/** The tool has run: a null product now means not sold. */
	matched: boolean;
	quantities: Map<string, number>;
	onQuantity: SetQuantity;
	onOpen: (product: ProductCard) => void;
}) {
	const { product } = line;
	return (
		<li className="flex min-h-15 items-center gap-3 py-2">
			{product ? (
				<IngredientPhoto product={product} onOpen={onOpen} />
			) : (
				<span
					aria-hidden
					className={cn(
						"size-11 shrink-0 rounded-xl bg-(--shop-brand-soft)",
						!matched && "animate-pulse",
					)}
				/>
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<p
					dir="auto"
					className="font-semibold text-(--shop-ink) text-[14px] leading-snug"
				>
					{line.text}
				</p>
				{product ? (
					<IngredientProduct
						product={product}
						needed={line.quantity}
					/>
				) : matched ? (
					<p className="text-(--shop-muted) text-xs">
						Not in the store
					</p>
				) : (
					<span className="h-3 w-28 animate-pulse rounded bg-(--shop-brand-soft)" />
				)}
			</div>
			{product?.inStock ? (
				<IngredientAction
					product={product}
					needed={line.quantity}
					quantity={quantities.get(product.id) ?? 0}
					onQuantity={onQuantity}
				/>
			) : null}
		</li>
	);
}

function IngredientPhoto({
	product,
	onOpen,
}: {
	product: ProductCard;
	onOpen: (product: ProductCard) => void;
}) {
	return (
		<button
			type="button"
			onClick={() => onOpen(product)}
			aria-label={`Details for ${product.title}`}
			className="size-11 shrink-0 overflow-hidden rounded-xl bg-white outline-none ring-1 ring-(--shop-brand-line) focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40"
		>
			{product.imageUrl ? (
				<img
					src={product.imageUrl}
					alt=""
					loading="lazy"
					decoding="async"
					className="size-full object-contain p-1"
				/>
			) : (
				<NoPhoto />
			)}
		</button>
	);
}

/** The matched product's name and what the line costs: "2 × $1.50" for two packs. */
function IngredientProduct({
	product,
	needed,
}: {
	product: ProductCard;
	needed: number;
}) {
	return (
		<p className="flex min-w-0 items-baseline gap-1.5 text-(--shop-muted) text-xs">
			<span dir="auto" className="truncate">
				{product.title}
			</span>
			<span
				className={cn(
					"shrink-0 font-semibold tabular",
					product.salePrice
						? "text-(--shop-deal)"
						: "text-(--shop-ink)",
				)}
			>
				{needed > 1 ? `${needed} × ` : ""}
				{formatMoney(
					product.salePrice ?? product.price,
					product.currency,
				)}
			</span>
		</p>
	);
}

/** Add the packs the line needs, or the basket count once it is in. */
function IngredientAction({
	product,
	needed,
	quantity,
	onQuantity,
}: {
	product: ProductCard;
	needed: number;
	quantity: number;
	onQuantity: SetQuantity;
}) {
	const [adding, run] = usePending();
	if (quantity > 0) {
		return (
			<QuantityStepper
				title={product.title}
				quantity={quantity}
				onChange={(next) => onQuantity(product.id, next)}
				className="w-28 shrink-0"
			/>
		);
	}
	return (
		<button
			type="button"
			disabled={adding}
			onClick={() => run(() => onQuantity(product.id, needed))}
			aria-label={`Add ${needed > 1 ? `${needed} of ` : ""}${product.title} to basket`}
			className="press flex size-10 shrink-0 items-center justify-center rounded-(--shop-btn) bg-(--shop-card) text-(--shop-brand-text) outline-none ring-[1.5px] ring-(--shop-brand-line) hover:ring-(--shop-brand) focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40 disabled:opacity-70"
		>
			{adding ? (
				<Spinner className="size-4" />
			) : (
				<PlusIcon className="size-5" strokeWidth={2.6} />
			)}
		</button>
	);
}

function Meta({
	icon: Icon,
	children,
}: {
	icon: typeof UsersIcon;
	children: React.ReactNode;
}) {
	return (
		<li className="flex items-center gap-1 tabular">
			<Icon className="size-3.5" aria-hidden />
			{children}
		</li>
	);
}
